import { useImage } from "@hooks/useImage";
import { useRichText } from "@hooks/useRichText";
import {
  type ComponentProps,
  type FromSanityGraphQL,
  type GatsbyCustomImage,
} from "@ts/components";
import { type SectionResponsiveSwiper } from "@ts/sanity";
import React, { FC } from "react";

export type ResponsiveSwiperInputProps = ComponentProps &
  Omit<
    FromSanityGraphQL<SectionResponsiveSwiper>,
    "desktopImages" | "mobileImages"
  > & {
    desktopImages?: (GatsbyCustomImage | undefined)[];
    mobileImages?: (GatsbyCustomImage | undefined)[];
  };

export type ResponsiveSwiperOutputProps = ResponsiveSwiperInputProps;

export const withResponsiveSwiper =
  (Component: FC<ResponsiveSwiperOutputProps>) =>
  ({
    name = "ResponsiveSwiper",
    analytics,
    content,
    desktopImages,
    mobileImages,
    styles,
    ...rest
  }: ResponsiveSwiperInputProps) => {
    const { getFluidImage } = useImage();
    const { parseContent } = useRichText();

    const normalisedContent = parseContent(content);
    const normalisedDesktopImages = desktopImages?.map((image) =>
      getFluidImage(image, { width: 2000, layout: "fullWidth" }),
    );
    const normalisedMobileImages = mobileImages?.map((image) =>
      getFluidImage(image, { width: 768, layout: "constrained" }),
    );

    if (!normalisedDesktopImages?.length && !normalisedMobileImages?.length) {
      return null;
    }

    Component.displayName = name;

    return (
      <Component
        {...rest}
        content={normalisedContent}
        desktopImages={normalisedDesktopImages}
        mobileImages={normalisedMobileImages}
      />
    );
  };
